import { Injectable } from '@angular/core';
import { PlatformService } from './platform.service';
import { logger } from 'src/app/common/services/logging/logging.service';
import { ExternalSettings } from 'src/app/common/services/external-settings/external-settings.interface';
import { AsyncSubject, defer, firstValueFrom, forkJoin, from, Observable, Subject, switchMap, take } from 'rxjs';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { MEDoNothingBasicResponseHandler, ResponseCode } from 'src/app/common/services/api/me-api-response';
import { Device, DeviceInfo } from '@capacitor/device';
import { DeviceInfoService } from './device-info.service';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { ExternalSettingsService } from 'src/app/common/services/external-settings/external-settings.service';
import { UserService } from 'src/app/common/services/user/user.service';

/**
 * Don't follow
 *  https://capacitorjs.com/docs/apis/push-notifications which will result unknown token
 * 
 * Should Follow 
 *  https://capacitorjs.com/docs/guides/push-notifications-firebase since we are connect to Firebase
 * 
 * 
 * 1) Please note that there are two static Subject for notification and action perform
 * 2) For Andorid, we need to store the notification in hash and restore it when action perform
 * 3) For IOS, we can directly use the notification object
 * 4) For journey that require subscribe Push Notification, they can subscribe the notification$ or actionPerformNotification$
 * 5) Cold start (iOS + Android): Capacitor retains pushNotificationActionPerformed until the first
 *    JS listener is attached. That can fire before Angular subscribers exist, so action performs
 *    are buffered until consumed. iOS also captures launchOptions in AppDelegate / AppViewController.
 */


@Injectable({
    providedIn: 'root'
})
export class PushNotificationService {

    private pushMessageHash = new Map<string, PushNotificationSchema>();

    readonly API_SUBMIT_SAS_TOKEN: string = MEAPIService.BASE_URL + '/customer/submitSasToken';
    readonly API_REVOKE_SAS_TOKEN: string = MEAPIService.BASE_URL + '/customer/revokeSasToken?token=';
    readonly API_RESET_PUSH_TOKENS: string = MEAPIService.BASE_URL + '/customer/resetPushTokens';

    private static notification$ = new Subject<PushMessage>();
    private static actionPerformNotification$ = new Subject<PushActionPerform>();
    /** Cold-start taps that arrived before any Angular subscriber was attached. */
    private static pendingActionPerforms: PushActionPerform[] = [];
    /** Tokens already shown or claimed by a push tap or resume poll. */
    private static handledTokens = new Set<string>();

    private externalSettings!: ExternalSettings;
    private lastRegistrationToken: Token | null = null;
    private suppressNextSasSubmit = false;
    private registrationToken$ = new Subject<Token>();

    constructor(
        private srvUser: UserService,
        private srvApi: MEAPIService,
        private srvES: ExternalSettingsService
    ) { }

    public init() {

        if (!PlatformService.isMobile()) return;

        forkJoin([
            this.srvES.externalSettings$
        ]).subscribe(([externalSettings]) => {
            this.externalSettings = externalSettings;

            if (!externalSettings.globalVariables.isModuleEnabledPushNotifications) return;
            // Register the Event Listener
            this.addPushNotificationListeners();

            // Register the Push Notification is status change to Login
            this.srvUser.isLoggedIn$.subscribe((isLoggedIn) => {
                if (isLoggedIn) {
                    this.registerPushNotification();
                }
            });
        });

        // When the app receives a push notification
        PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
            logger.debug('Push notification received: ', notification);
            this.dispatchMessage(notification);
        });

        // User Click On the notification
        PushNotifications.addListener('pushNotificationActionPerformed', notification => {
            logger.debug('Push notification action performed', notification.actionId, notification.inputValue);
            this.dispatchActionPerformed(notification);
        });


    }

    /**
     * Expose 2 static Subject for notification and action perform
     */
    public static getNotifications() {
        return PushNotificationService.notification$.asObservable();
    }

    public static getActionPerformNotifications(): Observable<PushActionPerform> {
        return new Observable<PushActionPerform>((subscriber) => {
            // Deliver any cold-start action that fired before this subscription.
            const pending = PushNotificationService.pendingActionPerforms.splice(0);
            for (const action of pending) {
                subscriber.next(action);
            }

            const sub = PushNotificationService.actionPerformNotification$.subscribe(subscriber);
            return () => sub.unsubscribe();
        });
    }

    /**
     * Claim a decrypted notification token so a push tap and a resume poll
     * cannot both show the same approval. Returns false when the token is
     * missing or was already claimed.
     */
    public static claimToken(token: string | undefined | null): boolean {
        if (!token || PushNotificationService.handledTokens.has(token)) {
            return false;
        }
        PushNotificationService.handledTokens.add(token);
        return true;
    }

    /**
     * True when the OS has granted push notification receive permission (always true on browser).
     * On Android, also requires LocalNotifications.areEnabled() — PushNotifications.checkPermissions()
     * always reports granted on Android 12 and below even when the system toggle is off.
     */
    public static async hasReceivePermission(): Promise<boolean> {
        if (!PlatformService.isMobile()) {
            return true;
        }
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive !== 'granted') {
            return false;
        }
        if (PlatformService.isAndroid()) {
            const { value } = await LocalNotifications.areEnabled();
            return value;
        }
        return true;
    }

    /**
     * Current OS receive permission state (`prompt` | `prompt-with-rationale` | `granted` | `denied`).
     * On Android, maps display-disabled (areEnabled false) to `denied` when not still promptable,
     * so older Android (≤12) and settings-off cases use the settings-guidance dialog path.
     */
    public static async getReceivePermissionStatus(): Promise<string> {
        if (!PlatformService.isMobile()) {
            return 'granted';
        }
        const permStatus = await PushNotifications.checkPermissions();
        if (
            permStatus.receive === 'prompt'
            || permStatus.receive === 'prompt-with-rationale'
        ) {
            return permStatus.receive;
        }
        if (PlatformService.isAndroid()) {
            const { value } = await LocalNotifications.areEnabled();
            if (!value) {
                return 'denied';
            }
        }
        return permStatus.receive;
    }

    /**
     * Prompts the OS for push notification permission when needed.
     * Only call this from the dashboard enable-notification flow (bell → Enable),
     * and only when status is still prompt (not already denied).
     * On Android, re-checks LocalNotifications.areEnabled() before returning true
     * (requestPermissions is a no-op on Android 12 and below).
     * @returns true when receive permission is granted after the request.
     */
    public static async requestReceivePermission(): Promise<boolean> {
        if (!PlatformService.isMobile()) {
            return false;
        }

        let permStatus = await PushNotifications.checkPermissions();
        logger.debug(`Push notification permission before request: ${permStatus.receive}`);

        if (permStatus.receive === 'denied') {
            // OS will not show the prompt again.
            return false;
        }

        if (permStatus.receive !== 'granted') {
            permStatus = await PushNotifications.requestPermissions();
            logger.debug(`Push notification permission after request: ${permStatus.receive}`);
        }

        if (permStatus.receive !== 'granted') {
            return false;
        }

        if (PlatformService.isAndroid()) {
            const { value } = await LocalNotifications.areEnabled();
            logger.debug(`Android notifications areEnabled after request: ${value}`);
            return value;
        }

        return true;
    }

    /**
     * Registers for push when permission is already granted. Does not prompt the OS.
     */
    public async registerIfPermitted(): Promise<boolean> {
        if (!PlatformService.isMobile()) {
            return false;
        }
        if (!this.externalSettings?.globalVariables?.isModuleEnabledPushNotifications) {
            return false;
        }

        const granted = await PushNotificationService.hasReceivePermission();
        if (!granted) {
            logger.debug('Push notification permission not granted; skipped registration');
            return false;
        }

        await PushNotifications.register();
        return true;
    }

    /**
     * Deletes all push tokens for the user and registers the current device token.
     * Uses the same payload as submitSasToken.
     */
    public resetPushTokens(): Observable<boolean> {
        return defer(() => from(this.resolveTokenForReset())).pipe(
            switchMap(({ token, deviceInfo }) =>
                this.callResetPushTokens(this.getSubmiSasToken(token, deviceInfo))
            )
        );
    }

    private async registerPushNotification() {
        if (!PlatformService.isMobile()) return;
        if (!this.externalSettings.globalVariables.isModuleEnabledPushNotifications) return;

        logger.debug(`New User Login, Registering Push Notification`);

        // Do not request OS permission here — that is only triggered from the dashboard bell.
        const registered = await this.registerIfPermitted();
        if (!registered) {
            logger.error('User denied push notification permissions! Skipped registration');
        }
    }

    private async addPushNotificationListeners() {

        const deviceInfo = await Device.getInfo();

        PushNotifications.addListener('registration', token => {
            logger.debug('Registration token: ', token.value);
            this.lastRegistrationToken = token;
            this.registrationToken$.next(token);

            if (this.suppressNextSasSubmit) {
                this.suppressNextSasSubmit = false;
                logger.debug('Skipped SAS token submit during reset-push registration');
                return;
            }

            this.submitSasToken(this.getSubmiSasToken(token, deviceInfo)).subscribe(isSuccess => {
                logger.debug(`SAS Token stored successfully : ${isSuccess}`);
            });
        });

        PushNotifications.addListener('registrationError', err => {
            logger.error('Registration error: ', err.error);
        });
    }

    private async resolveTokenForReset(): Promise<{ token: Token; deviceInfo: DeviceInfo }> {
        const deviceInfo = await Device.getInfo();

        if (this.lastRegistrationToken) {
            return { token: this.lastRegistrationToken, deviceInfo };
        }

        this.suppressNextSasSubmit = true;
        const tokenPromise = firstValueFrom(this.registrationToken$.pipe(take(1)));
        const registered = await this.registerIfPermitted();
        if (!registered) {
            this.suppressNextSasSubmit = false;
            throw new Error('PUSH_PERMISSION_DENIED');
        }

        const token = await tokenPromise;
        if (!token) {
            throw new Error('PUSH_TOKEN_UNAVAILABLE');
        }
        return { token, deviceInfo };
    }

    private callResetPushTokens(bean: SubmitSasTokenBean): AsyncSubject<boolean> {
        return this.srvApi.BasicPost(this.API_RESET_PUSH_TOKENS, bean, [new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS)]);
    }

    private dispatchMessage(notification: PushNotificationSchema) {
        // Store the Notification Information into hash
        if (PlatformService.isAndroid()) {
            this.pushMessageHash.set(notification.id, notification);
        }

        let pushMessage: PushMessage = {
            raw: notification,
            data: notification.data
        }

        PushNotificationService.notification$.next(pushMessage);
    }

    private dispatchActionPerformed(notification: ActionPerformed) {
        // Prefer the foreground-cached schema on Android when available.
        // On cold/background start the hash is empty — Capacitor still delivers
        // the payload on notification.notification; do not overwrite it with undefined.
        let data: any = notification.notification;
        if (PlatformService.isAndroid()) {
            const cached = this.pushMessageHash.get(notification.notification.id);
            if (cached) {
                data = cached;
                this.pushMessageHash.delete(notification.notification.id);
            }
        }

        const pushActionPerform: PushActionPerform = {
            raw: notification,
            data
        };

        if (PushNotificationService.actionPerformNotification$.observed) {
            PushNotificationService.actionPerformNotification$.next(pushActionPerform);
        } else {
            logger.debug('Buffering push action perform until a subscriber attaches (cold start)');
            PushNotificationService.pendingActionPerforms.push(pushActionPerform);
        }
    }

    private getSubmiSasToken(token: Token, deviceInfo: DeviceInfo): SubmitSasTokenBean {
        let bean: SubmitSasTokenBean = {
            sasToken: token.value,
            sasDeviceType: this.getDeviceType(),
            deviceId: DeviceInfoService.getDeviceId(),
            deviceName: deviceInfo.model,
        }
        return bean;
    }

    private getDeviceType(): number {
        return PlatformService.isIOS() ? PushDeviceType.IOS : PushDeviceType.ANDROID;
    }

    private submitSasToken(bean: SubmitSasTokenBean): AsyncSubject<boolean> {
        return this.srvApi.BasicPost(this.API_SUBMIT_SAS_TOKEN, bean, [new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS)]);
    }

    private revokeSasToken(token: string): AsyncSubject<boolean> {
        return this.srvApi.BasicPost(this.API_REVOKE_SAS_TOKEN + token, {}, [new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS)]);
    }

}


export interface SubmitSasTokenBean {
    sasToken: string;
    sasDeviceType: number;
    deviceId: string;
    deviceName: string;
}

export interface PushActionPerform {
    raw: ActionPerformed
    data: any
}

export interface PushMessage {
    raw: PushNotificationSchema
    data: any
}

export enum PushDeviceType {
    IOS = 1,
    ANDROID = 2
}

export enum PushNotificationServerMessage {
    SIGNATURE_REQUEST = "There is a transaction that requires your signature."
}